import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  changeImportSetAside,
  commitImportJob,
  createImportDraft,
  createSetupDraft,
  checkSetupDraftImportRows,
  discardSetupDraft,
  downloadEmployeeImportTemplate,
  downloadImportSetAsideCsv,
  getImportJob,
  getSetupDraft,
  getSetupWizardStatus,
  launchSetupDraft,
  listCompanySetupStatuses,
  listImportJobs,
  listSetupDrafts,
  listImportHistory,
  loadSetupDemoData,
  saveCompanySetup,
  saveImportMapping,
  updateSetupDraft,
  uploadSetupDraftImport,
  validateImportJob,
} from '@/api/services/setupWizard.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import type {
  CompanySetupPayload,
  ImportMappingPayload,
  SetupDraftPayload,
} from '@/types/setup-wizard.types';

export const setupWizardKeys = {
  status: ['setup-wizard', 'status'] as const,
  companyStatus: (companyId: string | undefined) => ['setup-wizard', 'status', companyId] as const,
  companyStatuses: ['setup-wizard', 'company-statuses'] as const,
  drafts: ['setup-wizard', 'drafts'] as const,
  draft: (id: string | null) => ['setup-wizard', 'drafts', id] as const,
  imports: (companyId: string | undefined) => ['setup-wizard', 'imports', companyId] as const,
  importDetail: (id: string | null) => ['setup-wizard', 'imports', id] as const,
};

export function useSetupWizardStatus(companyId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyId ? setupWizardKeys.companyStatus(companyId) : setupWizardKeys.status,
    queryFn: () => getSetupWizardStatus(companyId),
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  });
}

export function useCompanySetupStatuses(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: setupWizardKeys.companyStatuses,
    queryFn: listCompanySetupStatuses,
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  });
}

export function useSetupDrafts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: setupWizardKeys.drafts,
    queryFn: listSetupDrafts,
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  });
}

export function useSetupDraft(id: string | null) {
  return useQuery({
    queryKey: setupWizardKeys.draft(id),
    queryFn: () => getSetupDraft(id!),
    enabled: Boolean(id),
  });
}

export function useCreateSetupDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SetupDraftPayload | undefined) => createSetupDraft(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      toast.success('Setup draft started');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUpdateSetupDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SetupDraftPayload }) =>
      updateSetupDraft(id, payload),
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.draft(draft._id) });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      toast.success('Draft saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDiscardSetupDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discardSetupDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      toast.success('Draft discarded');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUploadSetupDraftImport(draftId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadSetupDraftImport(draftId!, file),
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.draft(draft._id) });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      toast.success('File uploaded and checked');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useCheckSetupDraftImportRows(draftId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ importId, payload }: { importId: string; payload: ImportMappingPayload }) =>
      checkSetupDraftImportRows(draftId!, importId, payload),
    onSuccess: (draft) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.draft(draft._id) });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      toast.success('Rows checked');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useLaunchSetupDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => launchSetupDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.companyStatuses });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.status });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Company launched');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useImportJobs(companyId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: setupWizardKeys.imports(companyId),
    queryFn: () => listImportJobs(companyId),
    staleTime: 30_000,
    enabled: options?.enabled !== false,
  });
}

export function useImportHistory(page: number, limit: number) {
  return useQuery({
    queryKey: ['setup-wizard', 'import-history', page, limit] as const,
    queryFn: () => listImportHistory(page, limit),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useImportJob(id: string | null) {
  return useQuery({
    queryKey: setupWizardKeys.importDetail(id),
    queryFn: () => getImportJob(id!),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'validating' || status === 'committing' ? 2500 : false;
    },
  });
}

export function useSaveCompanySetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CompanySetupPayload) => saveCompanySetup(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.status });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.companyStatuses });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Setup saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useCreateImportDraft(companyId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => createImportDraft(file, companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.imports(companyId) });
      toast.success('Import draft created');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useSaveImportMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ImportMappingPayload }) =>
      saveImportMapping(id, payload),
    onSuccess: (job) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.importDetail(job._id) });
      queryClient.invalidateQueries({ queryKey: ['setup-wizard', 'imports'] });
      toast.success('Column mapping saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useValidateImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => validateImportJob(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.importDetail(id) });
      queryClient.invalidateQueries({ queryKey: ['setup-wizard', 'imports'] });
      toast.success('Row check started');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useCommitImportJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => commitImportJob(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.importDetail(id) });
      queryClient.invalidateQueries({ queryKey: ['setup-wizard', 'imports'] });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.status });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.companyStatuses });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Import started');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDownloadEmployeeTemplate(
  companyId?: string,
  draftId?: string,
  companyCode?: string
) {
  return useMutation({
    mutationFn: () => downloadEmployeeImportTemplate(companyId, draftId, companyCode),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'kaamco-employee-import-template.xlsx';
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Sets the failing rows of an import aside, or puts them back. Pass the
 * draft id for an import inside a setup draft.
 */
export function useImportSetAside(draftId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      importId,
      action,
    }: {
      importId: string;
      action: 'set-aside-failed' | 'restore-set-aside';
    }) => changeImportSetAside({ importId, draftId }, action),
    onSuccess: (_, { importId, action }) => {
      if (draftId) {
        queryClient.invalidateQueries({ queryKey: setupWizardKeys.draft(draftId) });
        queryClient.invalidateQueries({ queryKey: setupWizardKeys.drafts });
      } else {
        queryClient.invalidateQueries({ queryKey: setupWizardKeys.importDetail(importId) });
        queryClient.invalidateQueries({ queryKey: ['setup-wizard', 'imports'] });
      }
      toast.success(
        action === 'set-aside-failed'
          ? 'Rows set aside. Checking the rest again.'
          : 'Rows put back. Checking them again.'
      );
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** Saves the set-aside rows as a CSV named after the original file. */
export function useDownloadImportSetAside() {
  return useMutation({
    mutationFn: ({
      importId,
      draftId,
    }: {
      importId: string;
      draftId?: string | null;
      filename: string;
    }) => downloadImportSetAsideCsv({ importId, draftId }),
    onSuccess: (blob, { filename }) => {
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${filename.replace(/\.[^.]+$/, '') || 'import'} - rows to fix.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useLoadSetupDemoData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (force: boolean) => loadSetupDemoData(force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.status });
      queryClient.invalidateQueries({ queryKey: setupWizardKeys.companyStatuses });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('Demo data loaded');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
